import { EhrRePairBanner } from '@/components/integrations/ehr-re-pair-banner';
import EhrScheduleItem from '@/components/layout/Navigation/sessions/side-nav-list/integration/ehr-sessions-list/ehr-schedule-item';
import {
  isEHRAppointment,
  useRetrieveAppointmentOrSchedule,
} from '@/components/layout/Navigation/sessions/side-nav-list/integration/hooks/useRetrieveAppointmentOrSchedule';
import Loader from '@/components/ui/loader';
import { useMutateStatePostIntegrationWorkflowSession } from '@/hooks/mutation/integration/direct-backend/useMutatePostIntegrationWorkflowSession';
import { useQueryUserEHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { cn } from '@/lib/cn';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { isMobile } from 'react-device-detect';
import { useIntl } from 'react-intl';
import { useLinkEhrPatientToSession } from '../hooks/useLinkEhrPatientToSession';
import EhrSessionErrorResult from './ehr-session-error';
import EhrSessionItem from './ehr-session-item';
import EhrSessionNotFoundResult from './ehr-session-not-found';
import EhrSessionsListLoader from './ehr-sessions-list-loader';

export const EHRSessions = () => {
  const { t } = useTranslations();
  const intl = useIntl();
  // The re-pair refetch storm surfaces mid-session, so the reconnect banner also
  // belongs here, scoped to the connected provider (self-gates when not required).
  const { data: ehrProvider } = useQueryUserEHR(
    (data) => data?.ehr_profile?.ehr_provider
  );

  // The provider value is an un-localised backend enum string. Mirror
  // BPConnected's localised label (same message id) for Best Practice and fall
  // back to the provider's own name for the other legacy providers.
  const ehrBrandLabel =
    ehrProvider === EHRProvider.Best_Practice
      ? intl.formatMessage({
          id: 'LXJC30shNm',
          defaultMessage: 'Best Practice',
          description: 'Best Practice label',
        })
      : (ehrProvider ?? '');

  const {
    searchResult,
    isPending: isPendingRetrieveAppointmentOrSchedule,
    isError: isErrorRetrieveAppointmentOrSchedule,
    refetch: refetchRetrieveAppointmentOrSchedule,
  } = useRetrieveAppointmentOrSchedule();

  const { isPending: isPostIntegrationWorkflowSessionPending } =
    useMutateStatePostIntegrationWorkflowSession();

  const { isPending: isLinkEhrPatientToSessionPending, onLinkEhrPatientToSession } =
    useLinkEhrPatientToSession();

  const isPending =
    isPostIntegrationWorkflowSessionPending || isLinkEhrPatientToSessionPending;

  return (
    <div className={cn('relative h-full overflow-auto', isMobile && 'pb-8')}>
      <EhrRePairBanner provider={ehrProvider ?? ''} brand={ehrBrandLabel} />
      {searchResult && searchResult?.length > 0 ? (
        searchResult.map((item) => {
          // Old integrations
          if (isEHRAppointment(item)) {
            return (
              <EhrSessionItem
                key={item.record_id}
                appt={item}
                handleSessionStart={onLinkEhrPatientToSession}
              />
            );
          } else {
            return (
              <EhrScheduleItem
                key={item.appointment.provider_details.unique_identifier}
                patient={item.patient}
                appointment={item.appointment}
              />
            );
          }
        })
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          {isPendingRetrieveAppointmentOrSchedule ? (
            <>
              <Loader size={32} strokeWidth={3} className="text-text-secondary" />
              <TypographyP2 className="text-text-secondary">
                {t('sessions.loadingSessions')}
              </TypographyP2>
            </>
          ) : isErrorRetrieveAppointmentOrSchedule ? (
            <EhrSessionErrorResult onRetry={refetchRetrieveAppointmentOrSchedule} />
          ) : (
            <EhrSessionNotFoundResult />
          )}
        </div>
      )}
      {isPending && <EhrSessionsListLoader />}
    </div>
  );
};
